import { useRouter } from 'next/router'
import React, { FC, MouseEvent, useContext, useMemo } from 'react'
import styled, { ThemeContext } from 'styled-components'

import * as PT from 'modules/Pricing/types'

import RoundedButton from 'components/RoundedButton'

import { trackPreselectedPackage } from 'services/analytics'

import * as T from 'types'
import * as E from 'types/enums'

interface IContainerProps {
  disabled?: boolean
}
const Container = styled.div<IContainerProps>`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
`

const ComingSoonText = styled.div`
  font-weight: 900;
  font-size: 24px;
  line-height: 33px;
  color: ${props => props.theme.colors.gold600};
`

interface IButtonProps {
  onClick?: ((event: MouseEvent) => void) | undefined
  href?: string
}

interface IProps extends Partial<PT.IPricingPackageCardModeProps> {
  disabled?: boolean
  initialized?: boolean
  pricingPackage?: T.IPricingPackage
  purchased?: boolean
  purchasedPackage: E.PackageType | undefined
  selected?: boolean | undefined
  onClickSelect?: ((event: MouseEvent) => void) | undefined
}

const PackageCardPricingButton: FC<IProps> = ({
  mode,
  pricingPackage,
  onClickSelect,
  purchased,
  purchasedPackage,
  disabled,
  initialized,
  selected,
}) => {
  const theme = useContext(ThemeContext)
  const router = useRouter()

  const trackPreselectedPackageAndRedirect = (preselectedPackageName: string | undefined) => {
    if (preselectedPackageName) trackPreselectedPackage(preselectedPackageName)

    router.push('/listing/create')
  }

  let buttonProps: IButtonProps = onClickSelect
    ? { onClick: onClickSelect }
    : { onClick: () => trackPreselectedPackageAndRedirect(pricingPackage?.name) }

  if (purchased || disabled) {
    buttonProps = {}
  }

  if (!pricingPackage) return null

  const text: string = useMemo(() => {
    if (!onClickSelect) return 'Choose Plan'

    if (!initialized) return '  '

    if (purchased) return 'Purchased'

    let tempVal = `Select${selected ? 'ed' : ''}`
    // basic package is selected by default
    // return 'Select' as button text if other package is purchased already
    if (purchasedPackage && pricingPackage.name === E.PackageType.basic) tempVal = 'Select'

    return tempVal
  }, [initialized, selected, purchased, purchasedPackage])

  const buttonBgColor: string = useMemo(() => {
    if (disabled && !purchased) {
      return theme.colors.grey
    }

    if (selected || purchased) {
      return theme.colors.yellow2
    }

    return theme.colors.blue
  }, [disabled, selected, purchased])

  return (
    <Container disabled={disabled}>
      {mode === E.PackageCardMode.ComingSoon
        ? (
          <ComingSoonText>COMING SOON!</ComingSoonText>
        )
        : (
          <RoundedButton
            href="/listing/create"
            dataTestId={`${pricingPackage.name}`}
            text={text}
            background={buttonBgColor}
            color={theme.colors.white}
            height={44}
            radius={20}
            fontSize={12}
            lineHeight={16}
            {...buttonProps}
            bold
            fullWidth
            center
          />
        )}
    </Container>
  )
}

export default PackageCardPricingButton
